import { RestaurantTable } from "@/shared/utils/interfaces";
import { Rect as RectType } from "konva/lib/shapes/Rect";
import { Transformer as TransformerType } from "konva/lib/shapes/Transformer";
import { useEffect, useRef } from "react";
import { Circle, Group, Rect, Text, Transformer } from "react-konva";
import useTable from "./useTable";

interface TableProps extends RestaurantTable {
  onChange: (shapeProps: RestaurantTable) => void;
  readOnly?: boolean;
  onTableClick?: (table: RestaurantTable) => void;
  hasSession?: boolean;
  customSelectedTable?: number[];
}

export const toFloat = (num: number) => parseFloat(num.toFixed(3));

const Table = ({
  readOnly = false,
  hasSession = false,
  onChange,
  onTableClick,
  customSelectedTable = [],
  ...table
}: TableProps) => {
  const {
    item_id,
    width,
    height,
    x,
    y,
    table_number,
    number_of_seats_above,
    number_of_seats_below,
    number_of_seats_left,
    number_of_seats_right,
  } = table;
  const key = `${width}-${height}`;

  const isCustomSelected = customSelectedTable.includes(table.item_id);
  const [selectedTable, setTable] = useTable();
  const isSelected = selectedTable?.item_id === item_id;
  const shapeRef = useRef<RectType>(null);
  const trRef = useRef<TransformerType>(null);

  const onSelect = () => {
    if (onTableClick) onTableClick(table);
    if (!readOnly) setTable(table);
  };

  useEffect(() => {
    setTable(undefined);
  }, [key]);

  useEffect(() => {
    if (isSelected) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const chairRadius = 30;
  const chairOffset = 5;
  const { amount_of_seats } = table;

  // Bereken maximaal aantal stoelen per zijde
  const chairsPerWidth = Math.floor(width / (chairRadius * 2 + chairOffset));
  const chairsPerHeight = Math.floor(height / (chairRadius * 2 + chairOffset));

  const renderBasedOnSettings = () => {
    const chairs: JSX.Element[] = [];

    // Helper: calculate effective seats for a side based on space
    const calcSeats = (
      requested: number,
      sideLength: number,
      seatSize: number,
    ) => {
      const maxPossible = Math.floor(sideLength / seatSize);

      if (maxPossible <= 0) return 0;
      if (maxPossible === 1 && requested > 0) return 1;

      return Math.min(requested, maxPossible);
    };

    const seatSizeWidth = width / chairsPerWidth; // space unit horizontally
    const seatSizeHeight = height / chairsPerHeight; // space unit vertically

    // A: TOP
    const topCount = calcSeats(number_of_seats_above, width, seatSizeWidth);
    for (let i = 0; i < topCount; i++) {
      const xPos = (i + 0.5) * (width / topCount);
      chairs.push(
        <Circle
          key={`top-${i}`}
          x={xPos}
          y={-chairRadius - chairOffset + 35}
          radius={chairRadius}
          fill="black"
        />,
      );
    }

    // B: BOTTOM
    const bottomCount = calcSeats(number_of_seats_below, width, seatSizeWidth);
    for (let i = 0; i < bottomCount; i++) {
      const xPos = (i + 0.5) * (width / bottomCount);
      chairs.push(
        <Circle
          key={`bottom-${i}`}
          x={xPos}
          y={height + chairRadius + chairOffset}
          radius={chairRadius}
          fill="black"
        />,
      );
    }

    // C: LEFT
    const leftCount = calcSeats(number_of_seats_left, height, seatSizeHeight);
    for (let i = 0; i < leftCount; i++) {
      const yPos = (i + 0.5) * (height / leftCount);
      chairs.push(
        <Circle
          key={`left-${i}`}
          x={-chairRadius - chairOffset + 35}
          y={yPos}
          radius={chairRadius}
          fill="black"
        />,
      );
    }

    // D: RIGHT
    const rightCount = calcSeats(number_of_seats_right, height, seatSizeHeight);
    for (let i = 0; i < rightCount; i++) {
      const yPos = (i + 0.5) * (height / rightCount);
      chairs.push(
        <Circle
          key={`right-${i}`}
          x={width + chairRadius + chairOffset}
          y={yPos}
          radius={chairRadius}
          fill="black"
        />,
      );
    }

    return chairs;
  };

  const renderAutomaticChairs = () => {
    const chairs: JSX.Element[] = [];
    let seatsLeft = amount_of_seats;

    // Eerst bovenkant
    const topCount = Math.min(chairsPerWidth, Math.ceil(seatsLeft / 2));
    for (let i = 0; i < topCount; i++) {
      const xPos = (i + 0.5) * (width / topCount);
      chairs.push(
        <Circle
          key={`top-${i}`}
          x={xPos}
          y={-chairRadius - chairOffset + 35}
          radius={chairRadius}
          fill="black"
        />,
      );
    }
    seatsLeft -= topCount;

    // Dan onderkant
    const bottomCount = Math.min(chairsPerWidth, seatsLeft);
    for (let i = 0; i < bottomCount; i++) {
      const xPos = (i + 0.5) * (width / bottomCount);
      chairs.push(
        <Circle
          key={`bottom-${i}`}
          x={xPos}
          y={height + chairRadius + chairOffset}
          radius={chairRadius}
          fill="black"
        />,
      );
    }
    seatsLeft -= bottomCount;

    // Daarna linkerkant
    const leftCount = Math.min(chairsPerHeight, Math.ceil(seatsLeft / 2));
    for (let i = 0; i < leftCount; i++) {
      const yPos = (i + 0.5) * (height / leftCount);
      chairs.push(
        <Circle
          key={`left-${i}`}
          x={-chairRadius - chairOffset + 35}
          y={yPos}
          radius={chairRadius}
          fill="black"
        />,
      );
    }
    seatsLeft -= leftCount;

    // Tenslotte rechterkant
    const rightCount = Math.min(chairsPerHeight, seatsLeft);
    for (let i = 0; i < rightCount; i++) {
      const yPos = (i + 0.5) * (height / rightCount);
      chairs.push(
        <Circle
          key={`right-${i}`}
          x={width + chairRadius + chairOffset}
          y={yPos}
          radius={chairRadius}
          fill="black"
        />,
      );
    }

    return chairs;
  };

  const renderChairs = () => {
    if (
      number_of_seats_above === undefined &&
      number_of_seats_below === undefined &&
      number_of_seats_left === undefined &&
      number_of_seats_right === undefined
    ) {
      return renderAutomaticChairs();
    } else {
      return renderBasedOnSettings();
    }
  };

  return (
    <Group
      onDragStart={readOnly ? undefined : onSelect}
      onDragEnd={
        readOnly
          ? undefined
          : (e) => {
              onChange({
                ...table,
                x: e.target.x().toString(),
                y: e.target.y().toString(),
              });
            }
      }
      width={width}
      height={height}
      draggable={!readOnly}
      x={parseInt(x)}
      y={parseInt(y)}
      key={`${width}-${height}`}
    >
      {/* Stoelen */}

      {renderChairs()}

      {/* Tafel */}
      <Rect
        onClick={onTableClick ? () => onTableClick(table) : onSelect}
        onTap={onSelect}
        ref={shapeRef}
        width={width}
        height={height}
        fill={
          isCustomSelected ? "#46c35f" : hasSession ? `#f44336` : table.color
        }
        onTransformEnd={
          readOnly
            ? undefined
            : () => {
                const node = shapeRef.current;
                const scaleX = node.scaleX();
                const scaleY = node.scaleY();
                node.scaleX(1);
                node.scaleY(1);
                onChange({
                  ...table,
                  x,
                  y,
                  width: Math.max(5, node.width() * scaleX),
                  height: Math.max(node.height() * scaleY),
                });
              }
        }
      />

      {isSelected && !readOnly && (
        <Transformer
          ref={trRef}
          flipEnabled={false}
          boundBoxFunc={(oldBox, newBox) => {
            if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}

      <Text
        text={`${table_number}`}
        fontSize={30}
        fill={"#ffffff"}
        fontStyle="bold"
        align="center"
        verticalAlign="middle"
        width={width}
        height={height}
        y={(height - 30) / 15}
        listening={false}
      />
    </Group>
  );
};

export default Table;
